<template>
  <div v-if="callId" class="mb-2 h-full rounded-md bg-white px-4 pb-4 pt-2 shadow-box">
    <h6 class="pb-1 text-small font-bold uppercase">{{ t('common.incoming-call') }}</h6>
    <div class="flex border-t-2 border-neutral-300 pt-2">
      <WtgAvatar :src="_profilePictureData" :initials="initials" size="sm" />
      <div class="mb-2 ml-4">
        <h4 class="text-large font-medium" v-if="name">
          {{ name }}
        </h4>
        <p class="text-small font-normal">{{ number }}</p>
      </div>
    </div>

    <div v-if="_toastData && _toastData.callMembers.length > 1">
      {{ t('fromconference', [_toastData.callMembers.length]) }}
    </div>
    <button
      @click="onCallAccept"
      class="mb-2 flex w-full items-center justify-center rounded-md bg-green py-1.5 text-white shadow-box hover:opacity-80 active:opacity-100"
    >
      <SvgIcon class="" icon="Phone" />
      <span class="ml-1 text-large">{{ t('common.accept') }}</span>
    </button>
    <button
      @click="onCallDecline"
      class="flex w-full items-center justify-center rounded-md bg-red-500 py-1.5 text-white shadow-box hover:opacity-80 active:opacity-100"
    >
      <SvgIcon class="" icon="HangUp" />
      <span class="ml-1 text-large">{{ t('common.reject') }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { onMounted, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { buildAuthorizationHeader } from '@/api/http';
import axios from 'axios';

import SvgIcon from '@/components/SvgIcon.vue';
import { V2_CONTACT_AVATAR } from '@/constants/endpoint';
import { getDomain } from '@/composables/useStorageSession';
import { builtDomainUrl } from '@/utils/httpUtils';
import WtgAvatar from '@/components/WtgAvatar.vue';

interface ToastQueryParams {
  callid?: string;
  initials?: string;
  name?: string;
  number?: string;
  profilePicture?: string;
  avatarUUID?: string;
}

type CallMember = {
  number: string;
  name: string | null;
  initials: string | null;
  status: 'active' | 'held' | 'dialing';
  profilePicture: string | null;
};

type ToastData = {
  callId: string;
  position: number;
  callMembers: CallMember[];
};

const { t } = useI18n();

const _toastData: Ref<ToastData | null> = ref(null);
const route = useRoute();
const { callid: callId, initials, name, number, avatarUUID } = route.query as ToastQueryParams;
const _profilePictureData = ref(null);

onMounted(() => {
  if (!avatarUUID) return;
  const url = `${V2_CONTACT_AVATAR}/${avatarUUID}`;
  fetchBlob(url)
    .then((r) => {
      _profilePictureData.value = r;
    })
    .catch((err) => console.error(err));
});

const onCallAccept = (): void => {
  if (!callId) {
    console.error('Cannot accept call: callId is missing');
    window.close();
    return;
  }

  const res = { callId: callId, status: true };
  window.wtgPhoneApi.sendToastResponse(res);
};

const onCallDecline = (): void => {
  if (!callId) {
    console.error('Cannot decline call: callId is missing');
    window.close();
    return;
  }

  const res = { callId: callId, status: false };
  window.wtgPhoneApi.sendToastResponse(res);
};

// Since the Toast is opened in new window on public (not authenticated page) the axios instance is not created and we need to create the whole config manually.
// Current authentication setup doesn't allow to redirect user through login page because if login checkbox is not clicked credentials are stored in SessionStorage
// Therefore they would not exist on toast page.
const fetchBlob = async (url?: string | null) => {
  try {
    const domain = getDomain();
    if (!domain) return Promise.reject('No domain');
    const baseURL = builtDomainUrl(domain);
    const response = await axios.get(`${baseURL}${url}`, { responseType: 'blob', headers: buildAuthorizationHeader() });
    if (response.data instanceof Blob) {
      return URL.createObjectURL(response.data);
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching blob:', error);
    // Rethrow error to be handled by Vue Query
    throw error;
  }
};
</script>
